import React from "react";
import { toast } from "react-toastify";
import { Input, Select } from "../../../components/form";
import { Button } from "../../../components/button";
import { PriceOrigin } from "../../../__generated__/globalTypes";
import { useCreateOrderLine } from "../hooks";
import { useProducts } from "../../products/hooks";
import { usePriceRules } from "../../price-rules/hooks";
import { priceOriginLabel } from "../../../helpers/enum-labels";

interface IProps {
  orderId: number;
  customerId?: number;
  onCompleted: () => void;
}

const DEFAULT_VAT_RATE = 5.5;

export const OrderLineForm: React.FC<IProps> = ({ orderId, customerId, onCompleted }) => {
  const { data: productsData } = useProducts({ where: {}, limit: 100, offset: 0 });
  const { form, submit, loading } = useCreateOrderLine({
    defaultValues: {
      orderId,
      priceOrigin: PriceOrigin.Manual,
      vatRate: DEFAULT_VAT_RATE,
    },
    onCompleted: () => {
      toast.success("Ligne ajoutée à la commande");
      form.reset({
        orderId,
        priceOrigin: PriceOrigin.Manual,
        vatRate: DEFAULT_VAT_RATE,
      });
      onCompleted();
    },
  });

  const productId = form.watch("productId");
  const { data: priceRulesData } = usePriceRules({
    where: productId ? { productId } : {},
  });

  const applicablePriceRules = (priceRulesData?.priceRules?.results ?? []).filter(
    (priceRule) => !priceRule.customerId || priceRule.customerId === customerId
  );

  const handleProductChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const productId = +e.target.value;
    const product = productsData?.products?.results?.find((p) => p.id === productId);
    if (product) {
      form.setValue("productId", productId);
      form.setValue("frozenLabel", product.name);
      // lastPrice est déjà exprimé en centimes, comme unitPriceCents
      form.setValue("unitPriceCents", Math.round(product.lastPrice));
      form.setValue("priceOrigin", PriceOrigin.Manual);
      form.setValue("appliedPriceRuleId", null);
    }
  };

  const handlePriceRuleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const priceRuleId = e.target.value ? +e.target.value : null;
    if (!priceRuleId) {
      form.setValue("appliedPriceRuleId", null);
      form.setValue("priceOrigin", PriceOrigin.Manual);
      return;
    }
    const priceRule = applicablePriceRules.find((rule) => rule.id === priceRuleId);
    if (priceRule) {
      form.setValue("appliedPriceRuleId", priceRuleId);
      form.setValue("priceOrigin", PriceOrigin.PriceRule);
      form.setValue("unitPriceCents", priceRule.priceCents);
    }
  };

  const quantity = form.watch("quantity");
  const unitPriceCents = form.watch("unitPriceCents");

  // Aperçu indicatif : le total de la ligne est calculé côté serveur
  const previewTotalCents =
    quantity && unitPriceCents ? Math.round(quantity * unitPriceCents) : 0;

  return (
    <div className="w-full">
      <div className="flex flex-wrap gap-3">
        <Select className="w-full" onChange={handleProductChange}>
          <option value="">Sélectionner un produit</option>
          {productsData?.products?.results?.map((product) => (
            <option key={product.id} value={product.id}>
              {product.name}
            </option>
          ))}
        </Select>
        {productId && (
          <Select
            label="Règle de prix (optionnel)"
            className="w-full"
            value={form.watch("appliedPriceRuleId") ?? ""}
            onChange={handlePriceRuleChange}
          >
            <option value="">Prix libre</option>
            {applicablePriceRules.map((priceRule) => (
              <option key={priceRule.id} value={priceRule.id}>
                {(priceRule.quantity ?? 1) > 1 ? `À partir de ${priceRule.quantity} - ` : ""}
                {(priceRule.priceCents / 100).toFixed(2)} €
                {priceRule.customerId ? " (client)" : " (générique)"}
              </option>
            ))}
          </Select>
        )}
        <Input
          label="Libellé"
          className="w-full"
          {...form.register("frozenLabel", { required: true })}
        />
        <Input
          label="Quantité"
          type="number"
          className="w-full"
          {...form.register("quantity", { required: true, valueAsNumber: true })}
        />
        <Input
          label="Prix unitaire (centimes)"
          type="number"
          className="w-full"
          helperText={`= ${((unitPriceCents || 0) / 100).toFixed(2)} €`}
          {...form.register("unitPriceCents", { required: true, valueAsNumber: true })}
        />
        <div className="w-full">
          <p className="label">Total ligne</p>
          <input
            className="input w-full"
            value={`${(previewTotalCents / 100).toFixed(2)} €`}
            disabled
          />
        </div>
        <Input
          label="Taux TVA (%)"
          type="number"
          step="0.1"
          className="w-full"
          {...form.register("vatRate", { required: true, valueAsNumber: true })}
        />
        <Select
          label="Origine du prix"
          className="w-full"
          value={form.watch("priceOrigin") ?? ""}
          disabled
        >
          {Object.keys(PriceOrigin).map((origin) => (
            <option key={origin} value={origin}>
              {priceOriginLabel(origin)}
            </option>
          ))}
        </Select>
      </div>
      <div className="w-full mt-4">
        <Button onClick={submit} loading={loading} disabled={loading}>
          Ajouter la ligne
        </Button>
      </div>
    </div>
  );
};
